import { Tree } from '@lezer/common';
import { ParsedModule, ShaderDefine } from './types';

import { makeLoadModule, makeLoadModuleWithCache } from '../util/shader';
import { makeBundleToAttribute, makeBundleToAttributes, makeBundleToBindings, makeAttributeToFields } from '../util/bundle';
import { makeTranspile } from '../util/transpile';

import { makeASTParser, compressAST, decompressAST } from './ast';
import { toTypeSymbol, toTypeArgs } from './type';
import { removeComments, removeWhiteSpace, renameLocals } from './minify';
import { parser } from './grammar/wgsl.js';

import LRU from 'lru-cache';
import zip from 'lodash/zip.js';

export { loadStaticModule, loadVirtualModule, bindEntryPoint } from '../util/shader';

/** LRU cache for parsed shader code */
export const makeModuleCache = (options: Record<string, any> = {}) => new LRU<number, ParsedModule>({
  max: 100,
  ...options,
});

export const DEFAULT_CACHE = makeModuleCache();

/** Parse WGSL code into lezer tree */
export const parseShader = (code: string): Tree => parser.parse(code);

/** Parse a code module into its in-memory representation (AST + symbol table) */
export const loadModule = makeLoadModule(parseShader, makeASTParser, compressAST, decompressAST);

/** Use cache to load modules */
export const loadModuleWithCache = makeLoadModuleWithCache(loadModule, DEFAULT_CACHE);

/** Make WGSL constant definitions */
export const defineConstants = (defs: Record<string, ShaderDefine>): string => {
  const out = [];
  for (const k in defs) if (k[0] !== '@' && defs[k] != null) out.push(`const ${k} = ${defs[k]};`);
  return out.join("\n");
}

/** Make WGSL constant definitions */
export const defineEnables = (enabled: string[]) => enabled.length ? `enable ${enabled.join(', ')};` : '';

/** Convert a bundle with a defined entry point to a definition for that attribute or type. */
export const bundleToAttribute = makeBundleToAttribute(toTypeSymbol, toTypeArgs);

/** Convert a bundle to a definition for all its linkable attributes. */
export const bundleToAttributes = makeBundleToAttributes(toTypeSymbol, toTypeArgs);

/** Convert a bundle to a definition for all its bindings. */
export const bundleToBindings = makeBundleToBindings(toTypeSymbol, toTypeArgs);

/** Convert an attribute to all its primitive fields. */
export const attributeToFields = makeAttributeToFields(toTypeSymbol, toTypeArgs);

// Whitespace / comment removal + local var shortening
const minifyCode = (code: string) => {
  code = removeComments(code);
  code = renameLocals(code);
  code = removeWhiteSpace(code);
  return code;
};

export const symbolDictionary = {
  A: 'at' as const,
  B: 'bindings' as const,
  C: 'vec4<f32>' as const,
  D: 'vec3<f32>' as const,
  E: 'exports' as const,
  F: 'func' as const,
  G: 'flags' as const,
  H: 'inferred' as const,
  I: 'identifiers' as const,
  J: 'imported' as const,
  K: 'imports' as const,
  L: 'linkable' as const,
  M: 'members' as const,
  N: 'name' as const,
  O: 'modules' as const,
  P: 'parameters' as const,
  Q: 'qual' as const,
  R: 'symbol' as const,
  S: 'symbols' as const,
  T: 'type' as const,
  U: 'struct' as const,
  V: 'variable' as const,
  W: 'visibles' as const,
  X: 'externals' as const,
  Y: 'types' as const,
  Z: 'attr' as const,
};

/** ES/CommonJS Transpiler */
export const transpileWGSL = makeTranspile('wgsl', 'wgsl', symbolDictionary, loadModule, compressAST, minifyCode);

/** Templated literal syntax:

```tsx
wgsl`...`
``` */
export const wgsl = (literals: TemplateStringsArray, ...tokens: string[]) => {
  const code = zip(literals, tokens).flat();
  return loadModuleWithCache(code.join(''), '<inline code>', 'auto');
};

/** Format `number` as WGSL `f32` */
export const f32 = (x: number) => {
  const s = x.toString();
  return (!s.match(/\./)) ? s + '.0' : s;
};
/** Format `number` as WGSL `u32` */
export const u32 = (x: number) => Math.round(x).toString() + 'u';
/** Format `number` as WGSL `i32` */
export const i32 = (x: number) => Math.round(x).toString();
